import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { RunnerPairingService } from '../../services/runner-pairing.service';
import { saveBlob } from '../../shared/file-download';
import { digitsOf, formatCode, isSixDigits, packageFileName, pairingErrorKey } from './runner-pairing';

type Ladezustand = 'loading' | 'loaded' | 'failed';

/** Ohne ladbares Recht bleibt das Code-Feld: Ob verbunden werden darf, entscheidet der Server. */
type Verbindungsrecht = 'loading' | 'allowed' | 'denied';

const STEPS = ['download', 'start', 'enter'] as const;

let nextId = 0;

@Component({
  selector: 'app-runner-connect-help',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatProgressSpinner, TranslatePipe],
  templateUrl: './runner-connect-help.component.html',
  styleUrl: './runner-connect-help.component.scss',
})
export class RunnerConnectHelpComponent implements OnInit {
  private readonly service = inject(RunnerPairingService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly closable = input(false);

  readonly connected = output<string>();
  readonly closed = output<void>();

  readonly steps = STEPS;
  readonly ids = {
    title: `runner-connect-title-${++nextId}`,
    code: `runner-connect-code-${nextId}`,
    hint: `runner-connect-hint-${nextId}`,
  };

  readonly right = signal<Verbindungsrecht>('loading');

  readonly packages = signal<string[]>([]);
  readonly packagesState = signal<Ladezustand>('loading');
  readonly downloading = signal<string | null>(null);
  readonly downloadStarted = signal<string | null>(null);
  readonly downloadFailed = signal(false);

  readonly code = signal('');
  readonly busy = signal(false);
  readonly connectError = signal<string | null>(null);
  readonly connectedRunner = signal<string | null>(null);

  readonly canSubmit = computed(() => isSixDigits(this.code()) && !this.busy());

  ngOnInit(): void {
    this.service
      .listProjectRunners(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (liste) => this.right.set(liste.canManage ? 'allowed' : 'denied'),
        error: () => this.right.set('allowed'),
      });
    this.service
      .listPackages()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (platforms) => {
          this.packages.set(platforms);
          this.packagesState.set('loaded');
        },
        error: () => this.packagesState.set('failed'),
      });
  }

  onCodeInput(field: HTMLInputElement): void {
    const formatted = formatCode(field.value);
    field.value = formatted;
    this.code.set(formatted);
  }

  submit(): void {
    if (!this.canSubmit()) {
      return;
    }
    this.busy.set(true);
    this.connectError.set(null);
    this.connectedRunner.set(null);
    this.service
      .confirm(digitsOf(this.code()), this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (confirmation) => {
          this.connectedRunner.set(confirmation.runnerName);
          this.code.set('');
          this.busy.set(false);
          this.connected.emit(confirmation.runnerName);
        },
        error: (failure: HttpErrorResponse) => {
          this.connectError.set(pairingErrorKey(failure.status));
          this.busy.set(false);
        },
      });
  }

  download(platform: string): void {
    this.downloading.set(platform);
    this.downloadStarted.set(null);
    this.downloadFailed.set(false);
    this.service
      .downloadPackage(platform)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => {
          saveBlob(blob, packageFileName(platform));
          this.downloadStarted.set(platform);
          this.downloading.set(null);
        },
        error: () => {
          this.downloadFailed.set(true);
          this.downloading.set(null);
        },
      });
  }
}
